import React, { useState, useEffect } from 'react';

import _ from 'lodash';

import { List, Button } from 'antd';
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';

import Comment from './comment';

import { NoteOwnerTypes } from '../../../../shared/constants/enums';
import { Note } from '../../../../shared/models';

import '../../styles.less';

export type CommentsProps = {
  items: Note[];
  showArchiveEnabled?: boolean;
  compactModeEnabled?: boolean;
  cancelAddComment: () => void;
  refresh: (res?: any) => void;
  parentNoteId?: string | null;
  baseRequestParams?: any;
  ownerType: NoteOwnerTypes | undefined;
  insuredId?: string | null | undefined;
  isSuperUserNotes?: boolean;
  controlConfig?: any;
  searchString?: string | null | undefined;
  notePolicies?: any;
};

const Comments: React.FC<CommentsProps> = ({
  items,
  showArchiveEnabled,
  compactModeEnabled,
  cancelAddComment,
  refresh,
  parentNoteId,
  baseRequestParams,
  ownerType,
  insuredId,
  isSuperUserNotes,
  controlConfig,
  searchString,
  notePolicies,
}) => {
  const pageSize = 3;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [comments, setComments] = useState<Note[]>(items);
  const [createDateSortDirection, setCreateDateSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    setComments(items);
  }, [items]);

  useEffect(() => {
    if (searchString && items && items.length > pageSize) {
      const commentIndex = _.findIndex(items, function (o) {
        return _.includes(o.subject, searchString);
      });

      if (commentIndex > 0) {
        const quotient = Math.floor((commentIndex + 1) / pageSize);
        const remainder = (commentIndex + 1) % pageSize;

        const pageIndex = remainder === 0 ? quotient : quotient + 1;

        setCurrentPage(pageIndex);
      }
    }
  }, [items, searchString]);

  const onChangeCommentPage = (page: number): void => {
    setCurrentPage(page);
  };

  const handleSortComments = (): void => {
    let direction = createDateSortDirection;

    if (direction === 'asc') {
      direction = 'desc';
    } else {
      direction = 'asc';
    }

    setCreateDateSortDirection(direction);

    if (comments && comments.length) {
      const commentsSorted = comments.sort(function (a, b) {
        if (direction === 'asc') {
          return new Date(a.createDate).getTime() - new Date(b.createDate).getTime();
        } else {
          return new Date(b.createDate).getTime() - new Date(a.createDate).getTime();
        }
      });

      setComments([...commentsSorted]);
    }
  };

  const renderTotal = (total: number, range: [number, number]): string =>
    `${range[0]}-${range[1]} of ${total} comments`;

  return (
    <List
      size="small"
      style={{ width: '100%' }}
      dataSource={comments}
      className="comments-list"
      header={
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
          <Button
            type="link"
            title={'Sort comments: ' + (createDateSortDirection === 'asc' ? 'New to Old' : 'Old to New')}
            onClick={() => handleSortComments()}
          >
            Create Date
            {createDateSortDirection === 'asc' ? (
              <SortAscendingOutlined style={{ marginLeft: '4px' }} />
            ) : (
              <SortDescendingOutlined style={{ marginLeft: '4px' }} />
            )}
          </Button>
        </div>
      }
      pagination={{
        size: 'small',
        onChange: (page) => onChangeCommentPage(page),
        pageSize: pageSize,
        current: currentPage,
        total: comments?.length || 0,
        showTotal: (total, range) => renderTotal(total, range),
      }}
      renderItem={(item) => (
        <Comment
          key={item.id}
          item={item}
          showArchiveEnabled={showArchiveEnabled}
          compactModeEnabled={compactModeEnabled}
          parentNoteId={parentNoteId}
          cancelAddComment={cancelAddComment}
          refresh={refresh}
          baseRequestParams={baseRequestParams}
          ownerType={ownerType}
          insuredId={insuredId}
          isSuperUserNotes={isSuperUserNotes}
          controlConfig={controlConfig}
          searchString={searchString}
          notePolicies={notePolicies}
        />
      )}
    />
  );
};

export default Comments;
